import CartItem from "../../../utils/CartItem";
import {
  CUSTOMER_ANALYTICS_EVENT,
  MAIN_OFFER_TYPE,
  OFFER_CATEGORY,
  OrderTypesKeysEnum,
  ROUNDING_ENUM,
} from "../../../utils/enums";
import { SuccessReportTypes } from "../../../utils/ErrorTypes";
import { DoubleLinkedList } from "../../../utils/help/linkedList/LinkedList";
import {
  AnalyticsExtraInfo,
  ItemDetailsType,
  OfferTypeV2,
  OutOfStockItem,
  SubItemType,
} from "../../../utils/types";
import type { LoyaltyCartState } from "./LoyaltyCartTypes";
import type { OrderCartV2LineAppliedOffer } from "./OrderCartV2Types";

export type RedirectToItem = {
  itemId?: number;
  subItemId?: number;
  itemFromListOffer?: OfferTypeV2;
};

export type BogoSteps = {
  totalSteps: number;
  currentStep: number;
};

export type CartLoyaltyV2SliceType = {
  cart: CartOutput;
  cartActions: CartAction[];
  redirectToItem?: RedirectToItem;
  selectedOfferId: number;
  isLoading: boolean;
  pendingOperations: number;
  selectedBuySubItem: CartItemType | undefined;
  selectedOfferSubItems: DoubleLinkedList<CartItemType>;
  selectedParentOffer: OfferTypeV2 | undefined;
  bogoSteps?: BogoSteps;
  pendingAppliedOfferAnalytics?: {
    event: CUSTOMER_ANALYTICS_EVENT;
    extra?: AnalyticsExtraInfo;
  };
  latestCartVersion: number;
  /**
   * Tip amount the customer explicitly chose on the checkout (tip) screen.
   * Kept separate from `cart.tips` so it survives navigating away from and back
   * to the checkout screen (online payment only).
   * - `undefined` → customer hasn't interacted yet; the default tip applies.
   * - `null`      → customer explicitly removed the tip.
   * - `number`    → customer-selected tip amount.
   */
  tipUserSelection?: number | null;
  /** Loyalty service (V2) — cart lines, offers, redemption session, apply flow */
  loyalty: LoyaltyCartState;
};

export type CartLoyaltyV2Serialized = {
  cart: CartOutput;
  cartActions: CartAction[];
  redirectToItem?: RedirectToItem;
  selectedOfferId: number;
  isLoading: boolean;
  selectedBuySubItem: CartItemType | undefined;
  selectedOfferSubItems: CartItemType[];
  selectedParentOffer: OfferTypeV2 | undefined;
  loyalty: LoyaltyCartState;
};

export type CartInput = {
  restaurantId: number;
  franchiseId: number;
  actions: CartAction[];
  hasRoundingMethodAndLevel: boolean;
  servicePercentage: number;
  vatPercentage: number;
  deliveryFees: number;
  orderType: OrderTypesKeysEnum;
  pointsPerOneCredit?: number;
  currencyRoundingLevel?: number;
  currencyRoundingMethod?: ROUNDING_ENUM;
  deliveryServicePercentageFees?: boolean;
  applyDiscountAfterVat?: boolean;
  calculateVatExcludingServiceFee?: boolean;
  isTaxInclusive?: boolean;
};

export type CartOutput = {
  items: CartItem[];
  subTotalPrice: number;
  deliveryFees: number;
  vat: number;
  service: number;
  tips: number;
  totalPrice: number;
  cartItemsNumber: number;
  redeemedOffer?: OfferPayload;
  isEligibleOffer: boolean;
  subTotalPriceWithoutExcludedFromOffers?: number;
  remainingToEligiable: number;
  discountedValue: number;
  isOfferApplied?: boolean;
};

export type CartItemType = {
  subItemDetails: SubItemType;
  itemDetails: ItemDetailsType;
  note: string;
  count: number;
  totalPrice: number;
  isFreeItem?: boolean;
  version: number;
  discountedItemPercentage?: number;
  customDeliveryTime?: number;
  excludeFromOffers?: boolean;
  excludeFromPointsPayment?: boolean;
  isOfferItem?: boolean;
  isBuyItem?: boolean;
  isBundleItem?: boolean;
  isDiscountedItem?: boolean;
  priceWithoutOffer?: number;
  lineId?: string;
  /** V2 loyalty path — from calculate response line `appliedOffer`. */
  appliedOffer?: OrderCartV2LineAppliedOffer;
  excludedFromTotals?: boolean;
};

export type OfferTypeBase = {
  id: number;
  offerType: MAIN_OFFER_TYPE;
  uiDetails: OfferUIDetails;
};

export type OfferDetailsBase = {
  id: number;
  minSpend: number;
};

export type ItemOfferDetails = OfferDetailsBase & {
  subItems: SubItemType[];
  count?: number;
  price?: number;
};

export type BogoOfferDetailsType = OfferDetailsBase & {
  buyItem: SubItemType;
  getOffer: OfferPayload;
};

export type FixedAmountOfferDetailsType = OfferDetailsBase & {
  value: number;
};

export type PercentageAmountOfferDetailsType = OfferDetailsBase & {
  value: number;
  cap: number;
};

export type MultiplierOfferDetailsType = OfferDetailsBase & {
  multiplier: number;
};

export type FreeItemOfferDetailsType = OfferDetailsBase & {
  count: number;
};

export type DiscountItemOfferDetailsType = OfferDetailsBase & {
  value: number;
};

export type BaseOfferPayload = {
  id: number;
  offerType: MAIN_OFFER_TYPE | string;
  offerCategory: OFFER_CATEGORY;
  details: OfferDetailsBase;
  uiDetails?: OfferUIDetails;
};

export type ItemOfferPayload = BaseOfferPayload & {
  item: CartItemType;
  items?: CartItemType[];
};

export type BogoOfferPayload = BaseOfferPayload & {
  offerType: MAIN_OFFER_TYPE.BOGO;
  buyItem: CartItemType;
  getOffer: OfferPayload;
};

export type AmountOfferPayload = BaseOfferPayload;

export type OfferPayload =
  | ItemOfferPayload
  | AmountOfferPayload
  | BogoOfferPayload;

export enum CartActionType {
  TIPS = "TIPS",
  ADD_ITEM = "ADD_ITEM",
  EDIT_ITEM = "EDIT_ITEM",
  REMOVE_ITEM = "REMOVE_ITEM",
  DELIVERY_FEES = "DELIVERY_FEES",
  CREATE_RECEIPT_ERROR = "CREATE_RECEIPT_ERROR",
  REDEEM_OFFER = "REDEEM_OFFER",
  REMOVE_OFFER = "REMOVE_OFFER",
}

export type createReceiptErrorType = {
  actionType: CartActionType.CREATE_RECEIPT_ERROR;
  error: {
    errorType: SuccessReportTypes;
  } & (
    | {
        errorType:
          | SuccessReportTypes.ReceiptController_ConfirmReceipt_ReqItemsContainsOutDatedItems
          | SuccessReportTypes.ItemService_ValidateOutOfStockSubItemIds_ReqItemsContainsOutOfStock;
        subItemIds: number[];
      }
    | {
        errorType: SuccessReportTypes.ReceiptController_ConfirmReceipt_itemsQuantityOutOfStock;
        outOfStockItems: OutOfStockItem[];
      }
  );
};

export type CartAction = {
  actionType?: CartActionType;
} & (
  | {
      actionType: CartActionType.TIPS;
      amount: number;
    }
  | {
      actionType: CartActionType.ADD_ITEM;
      item: CartItemType;
    }
  | {
      actionType: CartActionType.EDIT_ITEM;
      item: CartItemType;
      itemIndex: number;
    }
  | {
      actionType: CartActionType.REMOVE_ITEM;
      itemIndex: number | number[];
    }
  | {
      actionType: CartActionType.DELIVERY_FEES;
      amount: number;
    }
  | createReceiptErrorType
  | {
      actionType: CartActionType.REDEEM_OFFER;
      data: OfferPayload;
    }
  | {
      actionType: CartActionType.REMOVE_OFFER;
    }
);

export type OfferUIDetaislParams = {
  restaurantId: number;
  franchiseSlug: string;
  splitedOfferFromList: boolean;
  customerShortId?: string;
};

export type Localized<T> = {
  en?: T;
  ar?: T;
  "ar-SA"?: T;
  fr?: T;
};

export type LocalizedString = {
  en?: string;
  ar?: string;
  "ar-SA"?: string;
  fr?: string;
};

export enum GraphicTypeEnum {
  ICON = "icon",
  IMAGE = "image",
  TEXT = "text",
}

export type OfferCardText = {
  text: string;
  useAlternateStyle?: boolean;
};

export type OfferGraphic = {
  type: GraphicTypeEnum;
  value: string;
};

export type OfferCardType = {
  titleText: Localized<OfferCardText[]>;
  backgroundText: Localized<OfferCardText[]>;
  detailsText?: LocalizedString;
  shortDetailsText?: LocalizedString;
  graphics?: OfferGraphic[];
};

export type OfferUIDetails = {
  mainText: LocalizedString;
  titleText: LocalizedString;
  subText: LocalizedString;
  lockedSubText?: LocalizedString;
  buttonText: LocalizedString;
  graphic?: {
    url: any;
    type: GraphicTypeEnum;
    value: string[];
  };
  graphics?: OfferGraphic[];
  icon?: {
    type: GraphicTypeEnum;
    value: string[];
  };
  removeText: LocalizedString;
  removeButtonText: LocalizedString;
  offerCard: OfferCardType | OfferCardType[];
  cartCard: {
    label: LocalizedString;
    subText?: LocalizedString;
    icon?: string;
    price?: number;
    priceWithoutOffer?: number;
    showPriceWithoutOffer?: boolean;
  };
  cartBanner?: {
    title: LocalizedString;
    description?: LocalizedString;
  };
  description?: LocalizedString;
  customSubText?: LocalizedString;
};
